import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Avatar, Icon,
  Alert, Stack, Chip, Button, List, ListItem, ListItemText, ListItemSecondaryAction,
  Skeleton,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';

import {
  useKpisAdmin,
  useRecaudacionMensual,
  useTopDeudores,
} from '../../hooks/useDashboard';
import { formatCurrency, formatCuit } from '../../utils/format';

/**
 * Dashboard Admin/Operador (Fase 7.2).
 * KPIs reales + gráfico de recaudación mensual + top deudores.
 */
export default function DashboardAdmin() {
  const { data: kpis, isLoading: loadingKpis } = useKpisAdmin();
  const { data: recaudacion = [], isLoading: loadingChart } = useRecaudacionMensual();
  const { data: topDeudores = [], isLoading: loadingDeudores } = useTopDeudores(5);

  const kpiCards = [
    {
      label: 'Empresas activas',
      value: kpis?.empresas_activas ?? 0,
      icon: 'business',
      color: '#1565C0',
      link: '/empresas',
    },
    {
      label: 'Afiliados activos',
      value: kpis?.afiliados_activos ?? 0,
      icon: 'people',
      color: '#2E7D32',
      link: '/afiliados',
    },
    {
      label: 'Cupones pendientes',
      value: kpis?.cupones_pendientes ?? 0,
      sub: kpis && kpis.cupones_vencidos > 0
        ? `${kpis.cupones_vencidos} vencidos`
        : undefined,
      subColor: 'error',
      icon: 'receipt_long',
      color: '#ed6c02',
      link: '/cupones',
    },
    {
      label: 'Trx por conciliar',
      value: kpis?.transacciones_pre_confirmadas ?? 0,
      icon: 'swap_horiz',
      color: '#9c27b0',
      link: '/transacciones',
    },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Resumen general — datos en tiempo real
      </Typography>

      {/* KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {kpiCards.map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.label}>
            <Card
              component={RouterLink}
              to={k.link}
              sx={{
                borderLeft: `4px solid ${k.color}`,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${k.color}20`, color: k.color, width: 56, height: 56 }}>
                  <Icon sx={{ fontSize: 30 }}>{k.icon}</Icon>
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {k.label}
                  </Typography>
                  {loadingKpis ? (
                    <Skeleton width={50} height={40} />
                  ) : (
                    <Typography variant="h4" fontWeight={700}>{k.value}</Typography>
                  )}
                  {k.sub && (
                    <Typography variant="caption" color={`${k.subColor}.main`}>
                      {k.sub}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Línea financiera */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderTop: '4px solid #c62828' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Icon color="error">account_balance_wallet</Icon>
                <Typography variant="body2" color="text.secondary">
                  Total adeudado
                </Typography>
              </Stack>
              {loadingKpis ? (
                <Skeleton width={180} height={48} />
              ) : (
                <Typography variant="h4" fontWeight={700} color="error.main">
                  {formatCurrency(kpis?.total_adeudado ?? 0)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Suma de saldos pendientes de todas las empresas.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderTop: '4px solid #2e7d32' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Icon color="success">attach_money</Icon>
                <Typography variant="body2" color="text.secondary">
                  Recaudación de este mes
                </Typography>
              </Stack>
              {loadingKpis ? (
                <Skeleton width={180} height={48} />
              ) : (
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {formatCurrency(kpis?.recaudacion_mes_actual ?? 0)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Pagos acreditados durante el mes en curso.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráfico + Top deudores */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recaudación mensual (últimos 12 meses)
              </Typography>
              {loadingChart ? (
                <Skeleton variant="rectangular" height={280} />
              ) : recaudacion.every((r) => r.monto_emitido === 0 && r.monto_acreditado === 0) ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Sin movimientos en los últimos 12 meses. Cuando se generen
                  cupones y se acrediten pagos, este gráfico va a poblarse.
                </Alert>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={recaudacion} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="mes" tickFormatter={(v: string) => v.slice(2)} fontSize={11} />
                    <YAxis
                      tickFormatter={(v: number) =>
                        v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M`
                          : v >= 1_000 ? `${(v / 1_000).toFixed(0)}k`
                          : String(v)}
                      fontSize={11}
                    />
                    <ChartTooltip
                      formatter={(v) => formatCurrency(typeof v === 'number' ? v : Number(v))}
                      labelStyle={{ color: '#333' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="monto_emitido"    name="Emitido"    fill="#1565C0" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="monto_acreditado" name="Recaudado" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" fontWeight={600}>
                  Top deudores
                </Typography>
                <Button
                  component={RouterLink}
                  to="/cuenta-corriente"
                  size="small"
                >
                  Ver todos
                </Button>
              </Stack>

              {loadingDeudores ? (
                <Stack spacing={1}>
                  {[1, 2, 3].map((i) => <Skeleton key={i} height={50} />)}
                </Stack>
              ) : topDeudores.length === 0 ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Sin empresas con deuda. ¡Todo al día!
                </Alert>
              ) : (
                <List dense disablePadding>
                  {topDeudores.map((d) => (
                    <ListItem
                      key={d.empresa_id}
                      component={RouterLink}
                      to={`/cuenta-corriente/${d.empresa_id}`}
                      sx={{
                        px: 0, py: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <ListItemText
                        primary={d.empresa_razon_social}
                        secondary={formatCuit(d.empresa_cuit)}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <Stack alignItems="flex-end">
                          <Typography variant="body2" fontWeight={600} color="error.main">
                            {formatCurrency(d.saldo_total)}
                          </Typography>
                          {d.cant_vencidos > 0 && (
                            <Chip
                              label={`${d.cant_vencidos} venc.`}
                              size="small"
                              color="error"
                              sx={{ height: 16, fontSize: 10 }}
                            />
                          )}
                        </Stack>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
